import { createContext, useContext } from 'react';
import type { DockState, DockZone, EffectiveLayout, Rect, Size } from './layout';
import type { ToolWindowBadge, ToolWindowDefinition } from './registry';

/** The docking framework as the shell, pages and windows use it (spec 6.8). */
export interface DockApi {
  definitions: readonly ToolWindowDefinition[];
  state: DockState;
  effective: EffectiveLayout;
  /** Windows whose content the current page provides. */
  available: ReadonlySet<string>;
  badges: ReadonlyMap<string, ToolWindowBadge>;
  /** Below 1024 px (spec 6.8.9). */
  narrow: boolean;
  /** Below 768 px: windows open as bottom sheets (spec 6.8.9). */
  phone: boolean;
  /** The window open as the bottom sheet on phones. */
  sheet: string | null;
  workspace: Size;
  setWorkspace: (size: Size) => void;

  /** Status-bar rule: hidden → shown and focused; shown → focused; focused → hidden. */
  toggle: (id: string) => void;
  show: (id: string, options?: { focus?: boolean }) => void;
  hide: (id: string) => void;
  dock: (id: string, zone: DockZone, index?: number) => void;
  float: (id: string, rect?: Rect | null) => void;
  toggleMaximize: (id: string) => void;
  activate: (id: string) => void;
  resize: (zone: DockZone, size: number) => void;
  collapse: (zone: DockZone, collapsed: boolean) => void;
  move: (id: string, rect: Rect) => void;
  resetWindow: (id: string) => void;
  resetLayout: () => void;
  dockAll: () => void;
  hideAll: () => void;
  setView: (id: string, view: unknown) => void;
  announce: (text: string) => void;

  /** Content registration (ToolWindowContent): the last mounted provider of a window wins. */
  register: (id: string, token: string) => () => void;
  setBadge: (id: string, token: string, badge: ToolWindowBadge | undefined) => void;
  provider: (id: string) => string | undefined;
  /** A stable element per window that the window's frame shows and the content renders into. */
  host: (id: string) => HTMLElement;
}

export const DockContext = createContext<DockApi | null>(null);

export function useDock(): DockApi {
  const dock = useContext(DockContext);
  if (!dock) throw new Error('useDock needs a DockProvider.');
  return dock;
}

/** The dock, or null outside the shell (component tests that render a page alone). */
export function useOptionalDock(): DockApi | null {
  return useContext(DockContext);
}

/** DOM attributes that tie elements to their window (focus handling and shortcuts). */
export const ATTR = {
  frame: 'data-tool-window',
  tab: 'data-tool-window-tab',
  controls: 'data-tool-window-controls',
} as const;

/** The window that holds the focused element: its frame, its tab or its zone header's controls. */
export function focusedWindow(root: Document = document): string | null {
  const active = root.activeElement;
  if (!(active instanceof Element)) return null;
  const owner = active.closest(`[${ATTR.frame}], [${ATTR.tab}], [${ATTR.controls}]`);
  if (!owner) return null;
  return owner.getAttribute(ATTR.frame) ?? owner.getAttribute(ATTR.tab) ?? owner.getAttribute(ATTR.controls);
}

export function frameOf(id: string, root: Document = document): HTMLElement | null {
  return root.querySelector<HTMLElement>(`[${ATTR.frame}="${id}"]`);
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** The first control of the window's body (its toolbar first), else the window itself. */
export function firstControl(frame: HTMLElement): HTMLElement {
  const body = frame.querySelector<HTMLElement>('[data-tool-window-body]') ?? frame;
  return body.querySelector<HTMLElement>(FOCUSABLE) ?? frame;
}
